import { warnInDev } from './warnInDev';

/** A single sorted column. Compiled to the server's `sort=field,DIR` param. */
export interface CrudSort {
  field: string;
  direction: 'ASC' | 'DESC';
}

/**
 * One active filter chip: the UI operator id (spec §7.2's `is`, `between`, `in` — not the
 * server's `$`-prefixed form) and its value. Multi-value operators (`between`, `in`) carry
 * an array; single-value operators carry a string.
 */
export interface FilterValue {
  op: string;
  value: string | string[];
}

/** One chip per field, keyed by the server column name (spec §7.3's state shape). */
export type FilterState = Record<string, FilterValue>;

/**
 * The part of a list view that lives in the URL, so a filtered view is shareable and
 * survives reload. PR C adds a `filters` field here; the three below are stable.
 */
export interface CrudUrlState {
  page: number;
  search: string;
  sort: CrudSort[];
  filters: FilterState;
}

export const EMPTY_URL_STATE: CrudUrlState = { page: 1, search: '', sort: [], filters: {} };

/**
 * The URL keys one list view owns. Without a prefix these are the bare `page`, `s` and
 * `sort` — the shape every existing bookmark already carries, so the default must never
 * change. A resource sets `urlKeyPrefix` only when a route hosts more than one CRUD table,
 * at which point each table owns a disjoint set and they stop overwriting each other.
 */
export function crudUrlKeys(prefix?: string): { page: string; search: string; sort: string; f: string } {
  return prefix
    ? { page: `${prefix}_page`, search: `${prefix}_s`, sort: `${prefix}_sort`, f: `${prefix}_f` }
    : { page: 'page', search: 's', sort: 'sort', f: 'f' };
}

function decodePage(raw: string | null): number {
  if (raw === null) {
    return 1;
  }
  const value = Number(raw);
  if (!Number.isInteger(value) || value < 1) {
    warnInDev(`ignoring unusable page "${raw}" in the URL; showing page 1.`);
    return 1;
  }
  return value;
}

/**
 * `field,DIR` for one column, `field,DIR;field,DIR` for several. The single form
 * IS the legacy form, so every bookmark already in the wild keeps decoding.
 */
function decodeSort(raw: string | null): CrudSort[] {
  if (raw === null) {
    return [];
  }
  const out: CrudSort[] = [];
  for (const part of raw.split(';')) {
    const [field, direction, ...extra] = part.split(',');
    const trimmedField = field?.trim();
    const normalized = direction?.toUpperCase();
    if (extra.length > 0 || !trimmedField || (normalized !== 'ASC' && normalized !== 'DESC')) {
      warnInDev(`ignoring unusable sort "${part}" in the URL.`);
      continue;
    }
    // A field sorted twice is a contradiction; the first mention wins.
    if (!out.some((entry) => entry.field === trimmedField)) {
      out.push({ field: trimmedField, direction: normalized });
    }
  }
  return out;
}

/**
 * Decodes one `field:op:value` chip. Shape only — whether the field is filterable and the
 * op valid for its type lives with the resource's `filterable` config, and arity/parse
 * validation lives with compileFilter; a decoder that knew the schema would have to be
 * handed it on every read.
 *
 * The value segment is everything after the SECOND colon (so a value may itself contain
 * colons), and it is required for every operator except `isEmpty`, whose missing value IS
 * the predicate. Anything else malformed is dropped individually — never thrown (spec
 * §8.2) — so one stale chip cannot take the rest of a shared link down with it.
 */
function decodeFilterEntry(raw: string, validFields?: string[]): [string, FilterValue] | null {
  const fieldEnd = raw.indexOf(':');
  if (fieldEnd <= 0) {
    warnInDev(`ignoring unusable filter chip "${raw}" in the URL; dropping the chip.`);
    return null;
  }
  const field = raw.slice(0, fieldEnd);
  const rest = raw.slice(fieldEnd + 1);
  const opEnd = rest.indexOf(':');
  const op = opEnd === -1 ? rest : rest.slice(0, opEnd);
  if (op === '') {
    warnInDev(`ignoring unusable filter chip "${raw}" in the URL; dropping the chip.`);
    return null;
  }
  if (validFields && !validFields.includes(field)) {
    warnInDev(`ignoring filter chip for unknown field "${field}" in the URL; dropping the chip.`);
    return null;
  }
  if (op === 'isEmpty') {
    return [field, { op, value: '' }];
  }
  if (opEnd === -1 || rest.slice(opEnd + 1) === '') {
    warnInDev(`ignoring filter chip "${raw}" with no value in the URL; dropping the chip.`);
    return null;
  }
  const valueRaw = rest.slice(opEnd + 1);
  // The inverse of the encoder's comma join: `a1,b2` came from an array value, a comma-free
  // segment from a single one. A single value containing a literal comma is the format's
  // one accepted ambiguity — it decodes as an array and compiles back losslessly.
  const value: string | string[] = valueRaw.includes(',') ? valueRaw.split(',') : valueRaw;
  return [field, { op, value }];
}

function decodeFilters(entries: string[], validFields?: string[]): FilterState {
  const filters: FilterState = {};
  for (const raw of entries) {
    const decoded = decodeFilterEntry(raw, validFields);
    if (decoded) {
      const [field, value] = decoded;
      filters[field] = value;
    }
  }
  return filters;
}

/**
 * Never throws. A stale bookmark — a column since renamed, a hand-edited param — must
 * degrade to a slightly-wrong list, not a white screen, so each part is decoded
 * independently and a bad one falls back to its default while the others survive.
 */
export function decodeCrudUrlState(params: URLSearchParams, prefix?: string, validFields?: string[]): CrudUrlState {
  const keys = crudUrlKeys(prefix);
  return {
    page: decodePage(params.get(keys.page)),
    search: params.get(keys.search) ?? '',
    sort: decodeSort(params.get(keys.sort)),
    filters: decodeFilters(params.getAll(keys.f), validFields),
  };
}

const setOrDelete = (params: URLSearchParams, key: string, value: string | null) => {
  if (value === null) {
    params.delete(key);
  } else {
    params.set(key, value);
  }
};

/**
 * Writes the chips as repeated `f` params, one per entry (spec §8.1), deleting all of them
 * first so a removed chip actually disappears. Iteration follows Object.entries insertion
 * order, so the URL is byte-stable across renders instead of shuffling chips. A chip with
 * an empty value emits nothing (spec §7.4) — except `isEmpty`, whose missing value IS the
 * predicate and must survive the URL.
 */
function applyFilters(out: URLSearchParams, filters: FilterState, key: string) {
  out.delete(key);
  for (const [field, chip] of Object.entries(filters)) {
    if (!field || !chip?.op) {
      continue;
    }
    if (chip.op === 'isEmpty') {
      out.append(key, `${field}:${chip.op}`);
      continue;
    }
    const joined = Array.isArray(chip.value) ? chip.value.join(',') : chip.value ?? '';
    if (joined === '') {
      continue;
    }
    out.append(key, `${field}:${chip.op}:${joined}`);
  }
}

/**
 * Returns a NEW URLSearchParams: params this codec does not own (a tab, a deep-link
 * marker) are carried through untouched, and a value back at its default is deleted rather
 * than written, so a cleared view has a clean URL instead of `?page=1&s=`.
 */
export function applyCrudUrlState(params: URLSearchParams, next: CrudUrlState, prefix?: string): URLSearchParams {
  const keys = crudUrlKeys(prefix);
  const out = new URLSearchParams(params);
  setOrDelete(out, keys.page, next.page > 1 ? String(next.page) : null);
  setOrDelete(out, keys.search, next.search === '' ? null : next.search);
  setOrDelete(
    out,
    keys.sort,
    next.sort.length > 0 ? next.sort.map((entry) => `${entry.field},${entry.direction}`).join(';') : null
  );
  applyFilters(out, next.filters ?? {}, keys.f);
  return out;
}

/**
 * asc → desc → off for one column, leaving the others in place. A newly sorted
 * column joins at the END, so the click order is the precedence order.
 */
export function cycleSortList(current: CrudSort[], field: string): CrudSort[] {
  const existing = current.find((entry) => entry.field === field);
  if (!existing) {
    return [...current, { field, direction: 'ASC' }];
  }
  if (existing.direction === 'ASC') {
    return current.map((entry) => (entry.field === field ? { field, direction: 'DESC' as const } : entry));
  }
  return current.filter((entry) => entry.field !== field);
}
